import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { AccessControlService } from '../shared/access-control.service';
import { IAccessControlEntry, IAddAccessControlRequest } from '../shared/access-control.interface';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-allow-list',
  imports: [CommonModule, FormsModule, MatIconModule],
  templateUrl: './allow-list.component.html',
  styleUrl: './allow-list.component.scss'
})
export class AllowListComponent implements OnInit, OnDestroy {
  allEntries: IAccessControlEntry[] = [];
  filteredEntries: IAccessControlEntry[] = [];
  loading = false;
  errorMessage = '';
  successMessage = '';
  
  // Form data
  newEntry: IAddAccessControlRequest = {
    Type: 'AllowList',
    UserID: ''
  };
  
  // Filters
  selectedType = '';
  searchText = '';
  
  // Modal
  showConfirmModal = false;
  entryToRemove: IAccessControlEntry | null = null;
  
  private subscription = new Subscription();

  constructor(private accessControlService: AccessControlService) {}

  ngOnInit(): void {
    this.refreshData();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  refreshData(): void {
    this.loading = true;
    this.errorMessage = '';
    
    const sub = this.accessControlService.getAccessControlEntries().subscribe({
      next: (response) => {
        this.allEntries = response.accessControlEntries;
        this.filterEntries();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error fetching access control entries:', error);
        this.errorMessage = 'Failed to load access control entries. Please try again.';
        this.loading = false;
      }
    });
    
    this.subscription.add(sub);
  }

  addEntry(): void {
    if (!this.newEntry.UserID.trim()) {
      this.errorMessage = 'User ID is required.';
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';

    const sub = this.accessControlService.addAccessControlEntry(this.newEntry).subscribe({
      next: (response) => {
        this.successMessage = response.message;
        this.newEntry.UserID = ''; // Reset form
        this.refreshData();
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          this.successMessage = '';
        }, 3000);
      },
      error: (error) => {
        console.error('Error adding access control entry:', error);
        if (error.status === 409) {
          this.errorMessage = `User ${this.newEntry.UserID} is already in the ${this.newEntry.Type} list.`;
        } else {
          this.errorMessage = 'Failed to add entry. Please try again.';
        }
        this.loading = false;
      }
    });
    
    this.subscription.add(sub);
  }

  removeEntry(entry: IAccessControlEntry): void {
    this.entryToRemove = entry;
    this.showConfirmModal = true;
  }

  confirmRemove(): void {
    if (!this.entryToRemove) return;

    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';

    const request = {
      Type: this.entryToRemove.Type,
      UserID: this.entryToRemove.UserID
    };

    const sub = this.accessControlService.removeAccessControlEntry(request).subscribe({
      next: (response) => {
        this.successMessage = response.message;
        this.showConfirmModal = false;
        this.entryToRemove = null;
        this.refreshData();
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          this.successMessage = '';
        }, 3000);
      },
      error: (error) => {
        console.error('Error removing access control entry:', error);
        this.errorMessage = 'Failed to remove entry. Please try again.';
        this.showConfirmModal = false;
        this.entryToRemove = null;
        this.loading = false;
      }
    });
    
    this.subscription.add(sub);
  }

  cancelRemove(): void {
    this.showConfirmModal = false;
    this.entryToRemove = null;
  }

  filterEntries(): void {
    this.filteredEntries = this.allEntries.filter(entry => {
      const matchesType = !this.selectedType || entry.Type === this.selectedType;
      const matchesSearch = !this.searchText || 
        entry.UserID.toLowerCase().includes(this.searchText.toLowerCase()) ||
        entry.AddedBy.toLowerCase().includes(this.searchText.toLowerCase());
      
      return matchesType && matchesSearch;
    });
  }

  trackByEntryId(index: number, entry: IAccessControlEntry): string {
    return entry.PK;
  }

  formatDate(timestamp: number): string {
    return new Date(timestamp).toLocaleString();
  }
}
